import React, { useCallback, useEffect, useState } from 'react';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
import { ChartRendererSupabase } from '../charts/ChartRendererSupabase';
import fetchEducationalAttainmentByRaceSupabase from '../charts/EducationalAttainmentByRaceSupabase';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import fetchPercentageNoHealthInsuranceSupabase from '../charts/PercentageNoHealthInsuranceSupabase';
import { PageHeader } from '../layout/PageHeader';
import PercentageNoHealthInsuranceRowSupabase from '../rows/PercentageNoHealthInsuranceRowSupabase';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
// Import all chart components

import { HousingToEmployees } from '../charts/HousingToEmployees';
import PovertyByRaceTrends from '../charts/PovertyByRaceTrends';
import IncomeByRaceTrends from '../charts/IncomeByRaceTrends';
import IncomeByRaceBar from '../charts/IncomeByRaceBar';
import PovertyByRaceBar from '../charts/PovertyByRaceBar';
import GenericMapTiered from '../charts/GenericMapTiered';
import { CustomSectionsForPage } from '../customContent/CustomSectionsForPage';
import { CustomDatasetsForPage } from '../customContent/CustomDatasetsForPage';
import { useLocation } from '../../contexts/LocationContext';
import { useAdminPageSettings } from '../../hooks/useAdminPageSettings';
import JobWagesBySector from '../charts/JobWagesBySector';
import JobWagesBySectorTrends from '../charts/JobWagesBySectorTrends';
import JobWagesBySectorComparison from '../charts/JobWagesBySectorComparison';
import JobWageMedianTrendComparison from '../charts/JobWageMedianTrendComparison';
import EmploymentSection from '../charts/EmploymentSection';
import HousingBurdenSection from '../charts/HousingBurdenSection';
import { HealthAsthmaChart } from '../charts/HealthAsthmaChart';
import { HealthCardiovascularChart } from '../charts/HealthCardiovascularChart';
import { HealthLowBirthWeightChart } from '../charts/HealthLowBirthWeightChart';


interface PeopleProps {
  location?: string;
}

export const People: React.FC<PeopleProps> = ({ location: propLocation }) => {
  const { selectedLocation, selectedGeofips } = useLocation();
  const [chartData, setChartData] = useState<any>(null);
  const [educationalChartData, setEducationalChartData] = useState<any>(null);
  const [rowData, setRowData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<string>('overview');
  const [activeEducationalView, setActiveEducationalView] = useState<string>('overview');
  const [educationalAttainmentView, setEducationalAttainmentView] = useState<'chart' | 'map'>('chart');
  const [activeIncomeView, setActiveIncomeView] = useState<string>('overview');
  const [activeIncomeRaceChart, setActiveIncomeRaceChart] = useState<string>('income-trends');
  const [activeJobWagesView, setActiveJobWagesView] = useState<string>('current'); // chart variant
  const [activeJobWagesNarrative, setActiveJobWagesNarrative] =
    useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview'); // narrative panel
  const [activeHealthView, setActiveHealthView] = useState<string>('overview');
  const [activeHealthChart, setActiveHealthChart] = useState<'asthma' | 'cardiovascular' | 'lowBirthWeight'>('asthma');
  const [incomePovertyMetrics, setIncomePovertyMetrics] = useState<{
    incomeRatio: number | null;
    povertyRatio: number | null;
    highestPovertyRate: number | null;
    lowestIncome: number | null;
  }>({
    incomeRatio: null,
    povertyRatio: null,
    highestPovertyRate: null,
    lowestIncome: null,
  });
  const [jobWagesMetrics, setJobWagesMetrics] = useState<{
    highestWage: number | null;
    lowestWage: number | null;
    wageGapRatio: number | null;
    sectorsAboveCA: number | null;
    latestYear: number | null;
  }>({
    highestWage: null,
    lowestWage: null,
    wageGapRatio: null,
    sectorsAboveCA: null,
    latestYear: null,
  });
  const [educationalAttainmentMetrics, setEducationalAttainmentMetrics] = useState<{
    currentValue: number;
    californiaAverage: number;
    year: number;
    variable: string;
  } | null>(null);
  const [peopleMapMetrics, setPeopleMapMetrics] = useState<{
    currentValue: number;
    californiaAverage: number;
    year: number;
    variable: string;
  } | null>(null);
  const [activePeopleMapView, setActivePeopleMapView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');

  const currentLocation = propLocation || selectedLocation;
  const {
    isSectionEnabled,
    isChartEnabled,
    getSectionOrder,
    getChartOrder
  } = useAdminPageSettings('people', currentLocation);

  // Scroll to chart section when opening page with a hash (e.g. from livability/equity index).
  // Run only when main content is shown (!loading && !error) so the target section exists in the DOM.
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (loading || error) return;
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const t = setTimeout(scrollToHash, 150);
    const onHashChange = () => {
      if (!loading && !error) setTimeout(scrollToHash, 50);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => {
      clearTimeout(t);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, [loading, error]);

  const healthOptions = [
    { id: 'health_asthma', value: 'asthma', label: 'Asthma' },
    { id: 'health_cardiovascular', value: 'cardiovascular', label: 'Cardiovascular Disease' },
    { id: 'health_low_birth_weight', value: 'lowBirthWeight', label: 'Low Birth Weight' }
  ];
  const allowedHealthOptions = healthOptions
    .filter((option) => isChartEnabled('health_indicators', option.id))
    .sort((a, b) => getChartOrder('health_indicators', a.id) - getChartOrder('health_indicators', b.id));

  const educationOptions = [
    { id: 'education_chart', value: 'chart', label: 'Chart' },
    { id: 'education_map', value: 'map', label: 'Map' }
  ];
  const allowedEducationOptions = educationOptions
    .filter((option) => isChartEnabled('educational_attainment', option.id))
    .sort((a, b) => getChartOrder('educational_attainment', a.id) - getChartOrder('educational_attainment', b.id));

  const incomeOptions = [
    { id: 'income_trends', value: 'income-trends', label: 'Income Trends' },
    { id: 'poverty_trends', value: 'poverty-trends', label: 'Poverty Trends' },
    { id: 'income_by_race', value: 'income-bar', label: 'Income (Latest)' },
    { id: 'poverty_bar', value: 'poverty-bar', label: 'Poverty (Latest)' }
  ];
  const allowedIncomeOptions = incomeOptions
    .filter((option) => isChartEnabled('income_poverty_race', option.id))
    .sort((a, b) => getChartOrder('income_poverty_race', a.id) - getChartOrder('income_poverty_race', b.id));

  const jobWagesOptions = [
    { id: 'job_wages_current', value: 'current', label: 'Current Wages' },
    { id: 'job_wages_trends', value: 'trends', label: 'Wage Trends' },
    { id: 'job_wages_comparison', value: 'comparison', label: 'vs California' },
    { id: 'job_wages_median_trend', value: 'median-trend', label: 'Median vs CA/County' }
  ];
  const allowedJobWagesOptions = jobWagesOptions
    .filter((option) => isChartEnabled('economic_sector_wages', option.id))
    .sort((a, b) => getChartOrder('economic_sector_wages', a.id) - getChartOrder('economic_sector_wages', b.id));

  const peopleMapVariables = [
    { id: 'income', value: 'income' },
    { id: 'political_lean', value: 'political_lean' },
    { id: 'population_density', value: 'population_density' }
  ];
  const allowedPeopleMapVariables = peopleMapVariables
    .filter((option) => isChartEnabled('people_maps', option.id))
    .sort((a, b) => getChartOrder('people_maps', a.id) - getChartOrder('people_maps', b.id))
    .map((option) => option.value);
  const peopleMapsEnabled = isSectionEnabled('people_maps') && allowedPeopleMapVariables.length > 0;


  React.useEffect(() => {
    if (allowedHealthOptions.length && !allowedHealthOptions.some((opt) => opt.value === activeHealthChart)) {
      setActiveHealthChart(allowedHealthOptions[0].value as 'asthma' | 'cardiovascular' | 'lowBirthWeight');
    }
  }, [allowedHealthOptions, activeHealthChart]);

  React.useEffect(() => {
    if (allowedEducationOptions.length && !allowedEducationOptions.some((opt) => opt.value === educationalAttainmentView)) {
      setEducationalAttainmentView(allowedEducationOptions[0].value as 'chart' | 'map');
    }
  }, [allowedEducationOptions, educationalAttainmentView]);

  React.useEffect(() => {
    if (allowedIncomeOptions.length && !allowedIncomeOptions.some((opt) => opt.value === activeIncomeRaceChart)) {
      setActiveIncomeRaceChart(allowedIncomeOptions[0].value as 'income-trends' | 'poverty-trends' | 'income-bar' | 'poverty-bar');
    }
  }, [allowedIncomeOptions, activeIncomeRaceChart]);

  React.useEffect(() => {
    if (allowedJobWagesOptions.length && !allowedJobWagesOptions.some((opt) => opt.value === activeJobWagesView)) {
      setActiveJobWagesView(allowedJobWagesOptions[0].value as 'current' | 'trends' | 'comparison' | 'median-trend');
    }
  }, [allowedJobWagesOptions, activeJobWagesView]);

  useEffect(() => {
    if (!currentLocation) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load health insurance chart data
        const chartResult = await fetchPercentageNoHealthInsuranceSupabase(currentLocation);
        setChartData(chartResult);

        // Load educational attainment chart data
        const educationalChartResult = await fetchEducationalAttainmentByRaceSupabase(currentLocation);
        setEducationalChartData(educationalChartResult);

        // Load row data
        const rowResult = await PercentageNoHealthInsuranceRowSupabase(currentLocation);
        setRowData(rowResult);

      } catch (err) {
        console.error('Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentLocation]);

  // Fetch and calculate Income/Poverty metrics
  useEffect(() => {
    if (!currentLocation) return;

    const loadIncomePovertyMetrics = async () => {
      try {
        await supabaseLocationService.initialize();
        const geofips = await supabaseLocationService.getGeofips(currentLocation);
        if (!geofips) return;

        const geoid = String(geofips);

        // Find latest year for both tables
        const { data: incomeYears } = await supabaseClient
          .from('median_income_by_race')
          .select('year')
          .eq('geoid', geoid)
          .order('year', { ascending: false })
          .limit(1);

        const { data: povertyYears } = await supabaseClient
          .from('poverty_by_race')
          .select('year')
          .eq('geoid', geoid)
          .order('year', { ascending: false })
          .limit(1);

        const latestYear = Math.min(
          incomeYears && incomeYears.length > 0 ? Number(incomeYears[0].year) : 9999,
          povertyYears && povertyYears.length > 0 ? Number(povertyYears[0].year) : 9999
        );

        if (latestYear === 9999) return;

        // Fetch income data for latest year
        const { data: incomeData } = await supabaseClient
          .from('median_income_by_race')
          .select('race_or_ethnicity, median_income')
          .eq('geoid', geoid)
          .eq('year', latestYear);

        // Fetch poverty data for latest year
        const { data: povertyData } = await supabaseClient
          .from('poverty_by_race')
          .select('race, percentage')
          .eq('geoid', geoid)
          .eq('year', latestYear);

        if (!incomeData || incomeData.length === 0 || !povertyData || povertyData.length === 0) return;

        // Calculate income metrics
        const incomeValues = incomeData
          .map((r: any) => Number(r.median_income || 0))
          .filter((v: number) => v > 0);
        
        let incomeRatio = null;
        let lowestIncome = null;
        if (incomeValues.length > 0) {
          const highestIncome = Math.max(...incomeValues);
          lowestIncome = Math.min(...incomeValues);
          incomeRatio = highestIncome / lowestIncome;
        }

        // Calculate poverty metrics
        const povertyValues = povertyData
          .map((r: any) => Number(r.percentage || 0))
          .filter((v: number) => v > 0);
        
        let povertyRatio = null;
        let highestPovertyRate = null;
        if (povertyValues.length > 0) {
          highestPovertyRate = Math.max(...povertyValues);
          const lowestPovertyRate = Math.min(...povertyValues);
          povertyRatio = highestPovertyRate / lowestPovertyRate;
        }

        setIncomePovertyMetrics({
          incomeRatio,
          povertyRatio,
          highestPovertyRate,
          lowestIncome,
        });
      } catch (err) {
        console.error('Error loading income/poverty metrics:', err);
      }
    };

    loadIncomePovertyMetrics();
  }, [currentLocation]);

  // Fetch and calculate Job Wages metrics
  useEffect(() => {
    if (!currentLocation) return;

    const loadJobWagesMetrics = async () => {
      try {
        await supabaseLocationService.initialize();
        const geofips = await supabaseLocationService.getGeofips(currentLocation);
        if (!geofips) {
          console.warn('JobWagesMetrics: No geofips found for', currentLocation);
          return;
        }

        // Main sectors
        const MAIN_SECTORS = [
          'Management, business, science, and arts occupations',
          'Service occupations',
          'Sales and office occupations',
          'Natural resources, construction, and maintenance occupations',
          'Production, transportation, and material moving occupations'
        ];

        // Find latest year - use geofips (numeric) FIRST - this is the primary column
        let { data: years, error: yearsError } = await supabaseClient
          .from('job_by_wage_level')
          .select('year, geofips, location_name')
          .eq('geofips', geofips)
          .order('year', { ascending: false })
          .limit(1);

        // If no data by geofips, try geoid (text) as fallback
        if ((!years || years.length === 0) && !yearsError) {
          const geoid = String(geofips);
          const geoidResult = await supabaseClient
            .from('job_by_wage_level')
            .select('year, geofips, location_name')
            .eq('geoid', geoid)
            .order('year', { ascending: false })
            .limit(1);
          
          if (geoidResult.data && geoidResult.data.length > 0) {
            years = geoidResult.data;
            yearsError = geoidResult.error;
          }
        }

        // If still no data, try location_name
        if ((!years || years.length === 0) && !yearsError) {
          const nameResult = await supabaseClient
            .from('job_by_wage_level')
            .select('year, geofips, location_name')
            .ilike('location_name', `%${currentLocation}%`)
            .order('year', { ascending: false })
            .limit(1);
          
          if (nameResult.data && nameResult.data.length > 0) {
            years = nameResult.data;
            yearsError = nameResult.error;
            console.log('JobWagesMetrics: Found data by location_name, geofips:', nameResult.data[0].geofips);
          }
        }

        if (yearsError) {
          console.warn('JobWagesMetrics: Error fetching years:', yearsError);
          return;
        }

        const latestYear = years && years.length > 0 ? Number(years[0].year) : null;
        if (!latestYear) {
          console.warn('JobWagesMetrics: No years found for', currentLocation, 'geofips:', geofips);
          return;
        }

        // Use the geofips we found (might be from location_name lookup)
        const actualGeofips = years && years[0].geofips ? Number(years[0].geofips) : geofips;

        // Fetch local data - use geofips (numeric) FIRST
        let { data: allLocalData, error: localError } = await supabaseClient
          .from('job_by_wage_level')
          .select('job_type, median_income, gender')
          .eq('geofips', actualGeofips)
          .eq('year', latestYear);

        // If no data by geofips, try geoid (text) as fallback
        if ((!allLocalData || allLocalData.length === 0) && !localError) {
          const geoid = String(actualGeofips);
          const geoidResult = await supabaseClient
            .from('job_by_wage_level')
            .select('job_type, median_income, gender')
            .eq('geoid', geoid)
            .eq('year', latestYear);
          
          if (geoidResult.data && geoidResult.data.length > 0) {
            allLocalData = geoidResult.data;
            localError = geoidResult.error;
          }
        }

        // Fetch California data - use geofips (numeric) FIRST
        let { data: allCaData, error: caError } = await supabaseClient
          .from('job_by_wage_level')
          .select('job_type, median_income, gender')
          .eq('geofips', 6) // California geofips
          .eq('year', latestYear);

        // If no CA data by geofips, try geoid (text) as fallback
        if ((!allCaData || allCaData.length === 0) && !caError) {
          const caGeoidResult = await supabaseClient
            .from('job_by_wage_level')
            .select('job_type, median_income, gender')
            .eq('geoid', '6') // California geoid (text)
            .eq('year', latestYear);
          
          if (caGeoidResult.data && caGeoidResult.data.length > 0) {
            allCaData = caGeoidResult.data;
            caError = caGeoidResult.error;
          }
        }

        if (localError || caError) {
          console.warn('JobWagesMetrics: Error fetching data:', localError || caError);
          return;
        }

        if (!allLocalData || allLocalData.length === 0) {
          console.warn('JobWagesMetrics: No local data found for', currentLocation);
          return;
        }

        // Filter to main sectors and gender='both' (or any if 'both' doesn't exist)
        const hasBothGenderLocal = allLocalData.some((r: any) => (r.gender || '').toLowerCase() === 'both');
        const hasBothGenderCA = (allCaData || []).some((r: any) => (r.gender || '').toLowerCase() === 'both');

        const localData = allLocalData.filter((r: any) => {
          const jobType = (r.job_type || '').trim();
          const gender = (r.gender || '').trim();
          // Use some() to handle potential whitespace differences
          const isMainSector = MAIN_SECTORS.some(sector => sector.trim() === jobType);
          // If 'both' exists in data, only use 'both'. Otherwise, use any gender.
          const genderMatch = hasBothGenderLocal ? gender.toLowerCase() === 'both' : true;
          return isMainSector && genderMatch;
        });

        const caData = (allCaData || []).filter((r: any) => {
          const jobType = (r.job_type || '').trim();
          const gender = (r.gender || '').trim();
          // Use some() to handle potential whitespace differences
          const isMainSector = MAIN_SECTORS.some(sector => sector.trim() === jobType);
          // If 'both' exists in data, only use 'both'. Otherwise, use any gender.
          const genderMatch = hasBothGenderCA ? gender.toLowerCase() === 'both' : true;
          return isMainSector && genderMatch;
        });

        if (!localData || localData.length === 0) {
          const uniqueJobTypes = Array.from(new Set(allLocalData.map((r: any) => r.job_type)));
          console.warn('JobWagesMetrics: No matching sectors found. Available:', uniqueJobTypes.slice(0, 5));
          return;
        }

        // Calculate metrics (simple, inline)
        const wages = localData.map((r: any) => Number(r.median_income || 0)).filter((v: number) => v > 0);
        const highestWage = wages.length > 0 ? Math.max(...wages) : null;
        const lowestWage = wages.length > 0 ? Math.min(...wages) : null;
        const wageGapRatio = highestWage && lowestWage ? highestWage / lowestWage : null;

        // Compare to CA
        const caMap = new Map(caData?.map((d: any) => [d.job_type, Number(d.median_income || 0)]) || []);
        let sectorsAbove = 0;
        localData.forEach((local: any) => {
          const caWage = caMap.get(local.job_type) || 0;
          if (Number(local.median_income || 0) > caWage) sectorsAbove++;
        });

        setJobWagesMetrics({
          highestWage,
          lowestWage,
          wageGapRatio,
          sectorsAboveCA: sectorsAbove,
          latestYear
        });
      } catch (err) {
        console.error('Error loading job wages metrics:', err);
      }
    };

    loadJobWagesMetrics();
  }, [currentLocation]);

  const toggleView = (view: string) => {
    setActiveView(view);
  };

  const toggleEducationalView = (view: string) => {
    setActiveEducationalView(view);
  };

  const handlePeopleMapDataLoaded = useCallback((metrics: {
    currentValue: number;
    californiaAverage: number;
    year: number;
    variable: string;
  }) => {
    setPeopleMapMetrics(metrics);
  }, []);



  if (!currentLocation) {
    return (
      <div>
        <PageHeader 
          title="People & Demographics" 
          subtitle="Please select a location to view demographic data."
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <PageHeader 
          title="Loading..." 
          subtitle="Please wait while we load the data."
        />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader 
          title="Error" 
          subtitle={error}
        />
      </div>
    );
  }

  return (
    <div data-report-root="people">
      <PageHeader 
        title={`People & Demographics - ${currentLocation}`}
        subtitle="Comprehensive demographic insights and trends for the selected location."
        variant="people"
      />

      {/* Population Projection Section */}
      {/* Chart 1: Housing to Jobs Balance */}
      <HousingToEmployees 
        location={currentLocation}
        geofips={selectedGeofips || null}
        preferredState="California"
      />

      {/* Employment Section */}
      <EmploymentSection 
        location={currentLocation}
        geofips={selectedGeofips ? String(selectedGeofips) : null}
      />

      {/* Housing Burden Section */}
      <HousingBurdenSection 
        location={currentLocation}
        geofips={selectedGeofips ? String(selectedGeofips) : null}
      />

      {/* Health Insurance Section */}
      <ChartLayout
        scrollAnchorId="chart-health-insurance"
        title="Health Insurance Coverage"
        chartType="line"
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value={rowData?.indicator1 || '5.7%'} 
              label="Percent of residents with no health insurance in 2023"
              color="#1e3a8a"
            />
            <KeyMetric 
              value={rowData?.indicator2 || '-12.1%'} 
              label="Change in percentage points since 2012"
              color="#dc2626"
            />
          </KeyMetrics>
        }
        chartContent={
          chartData ? (
            <ChartRendererSupabase
              chartData={chartData.chartData}
              chartOptions={chartData.chartOptions}
              height="300px"
            />
          ) : (
            <div className="placeholder-chart">
              <p>Loading health insurance data...</p>
            </div>
          )
        }
        overviewContent={
          <OverviewContent
            title="Health Insurance Summary"
            description="This chart displays health insurance coverage rates for the selected location, showing trends over time and comparisons with state and county data."
          >
            <p>Across many counties and places, the percentage of people with health insurance began significantly decreasing between the years 2013 and 2018. For {currentLocation}, the percentage of people without health insurance decreased by 12% from 2012 to 2023. Health insurance coverage rates are a valuable indicator for local governments aligning climate action with public health and resilience goals.</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Health insurance coverage rates are a valuable indicator for local governments aligning climate action with public health and resilience goals."
          >
            <div className="methodology-details-simple">
              <h5>Climate-Health Integration</h5>
              <p>As climate impacts like extreme heat, wildfire smoke, and respiratory illness intensify, access to healthcare becomes essential for adaptation and resilience.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Framework</h5>
              <p>This supports integration of health equity into climate action planning under statewide mandates like SB 1000 and AB 32's environmental justice provisions.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Health insurance coverage varies significantly by race and income, shaping who can access care during climate-related emergencies."
          >
            <div className="methodology-details-simple">
              <h5>Vulnerability Assessment</h5>
              <p>Communities of color remain more likely to be uninsured or underinsured, leaving them vulnerable to worsening air quality, heat-related illness, and chronic disease exacerbated by climate change.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Action Planning</h5>
              <p>By examining insurance coverage alongside race and income, local governments can identify underserved populations and prioritize climate-health interventions that ensure all residents benefit from climate resilience strategies.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Health insurance data comes from the American Community Survey, providing detailed statistics on coverage rates across different demographic groups."
            dataSources={[
              'American Community Survey (ACS) 5-year estimates',
              'Table B27001: Health Insurance Coverage by Sex by Age',
              'Individuals without health insurance coverage',
              'Disaggregated by sex and age group'
            ]}
            methodology={[
              'Data represents percentage of population without health insurance coverage',
              'Calculated from ACS 5-year estimates for geographic areas',
              'Includes comparisons with state and county data when available',
              'Trends analyzed over multiple time periods'
            ]}
          />
        }
        activeView={activeView}
        onViewChange={toggleView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        onDownloadPNG={() => {
          const canvas = document.querySelector('#health-insurance-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            link.download = `Health_Insurance_Coverage_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          console.log('CSV download for Health Insurance not yet implemented');
        }}
        pngFilename={`Health_Insurance_Coverage_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`Health_Insurance_Data_${currentLocation.replace(/\s+/g, '_')}`}
      />

      {/* Health Section */}
      <ChartLayout
        scrollAnchorId="chart-health"
        title="Health Indicators"
        chartType="bar"
        chartSelector={{
          options: allowedHealthOptions.map((option) => ({ value: option.value, label: option.label })),
          value: activeHealthChart,
          onChange: (value) => setActiveHealthChart(value as 'asthma' | 'cardiovascular' | 'lowBirthWeight')
        }}
        chartContent={
          activeHealthChart === 'asthma' ? (
            <HealthAsthmaChart location={currentLocation} />
          ) : activeHealthChart === 'cardiovascular' ? (
            <HealthCardiovascularChart location={currentLocation} />
          ) : (
            <HealthLowBirthWeightChart location={currentLocation} />
          )
        }
        overviewContent={
          <OverviewContent
            title="Health Indicators Overview"
            description="Health indicators from CalEnviroScreen 4.0 provide insights into environmental health burdens affecting communities."
          >
            {activeHealthChart === 'asthma' ? (
              <p>Asthma rates reflect the prevalence of respiratory conditions that can be exacerbated by air pollution and climate-related factors like wildfire smoke. Higher asthma rates indicate greater vulnerability to poor air quality and climate impacts.</p>
            ) : activeHealthChart === 'cardiovascular' ? (
              <p>Cardiovascular disease rates indicate the prevalence of heart and circulatory system conditions. These conditions can be worsened by air pollution, extreme heat, and other climate-related stressors.</p>
            ) : (
              <p>Low birth weight rates reflect the percentage of babies born weighing less than 2,500 grams. This indicator is associated with environmental exposures and can be influenced by air quality, heat stress, and other environmental factors.</p>
            )}
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Health indicators help identify communities most vulnerable to climate impacts and prioritize climate action that protects public health."
          >
            <div className="methodology-details-simple">
              <h5>Vulnerability Assessment</h5>
              <p>Health indicators help identify populations most at risk from climate-related health impacts, enabling targeted interventions and resource allocation.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Climate-Health Integration</h5>
              <p>Understanding health burdens supports integration of health equity into climate action planning, ensuring climate policies protect and improve public health.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Health disparities reflect historical and ongoing environmental injustices that must be addressed through equitable climate action."
          >
            <div className="methodology-details-simple">
              <h5>Environmental Justice</h5>
              <p>Communities with higher health burdens often face greater exposure to pollution and climate risks. Equitable climate action must prioritize these communities for protection and benefits.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Health Equity</h5>
              <p>Climate policies should reduce health disparities by improving air quality, reducing heat exposure, and ensuring all communities have access to climate resilience resources.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Health indicators are sourced from CalEnviroScreen 4.0, California's environmental health screening tool."
            dataSources={[
              'CalEnviroScreen 4.0 Results',
              'California Office of Environmental Health Hazard Assessment (OEHHA)',
              'Aggregated by place, county, and state',
              'Data represents environmental health indicators'
            ]}
            methodology={[
              'Data from CalEnviroScreen 4.0 aggregated to city, county, and state levels',
              'Values represent health indicator rates for the selected location',
              'California state average calculated from state-level aggregation',
              'Comparison shows location relative to statewide average',
              'Higher values indicate greater health burden'
            ]}
          />
        }
        activeView={activeHealthView}
        onViewChange={(view: string) => setActiveHealthView(view)}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        overviewSectionId="people_equity"
        overviewChartId="income_by_race"
      />

      {/* Educational Attainment Section */}
      <ChartLayout
        scrollAnchorId="chart-educational-attainment-by-race"
        title="Educational Attainment"
        chartType="bar"
        keyMetrics={
          educationalAttainmentView === 'map' && educationalAttainmentMetrics ? (
            <KeyMetrics>
              <KeyMetric
                value={`${educationalAttainmentMetrics.currentValue.toFixed(1)}%`}
                label="Bachelor's Degree or Higher"
              />
              <KeyMetric
                value={`${educationalAttainmentMetrics.californiaAverage.toFixed(1)}%`}
                label="California Average"
              />
            </KeyMetrics>
          ) : (
            <KeyMetrics>
              <KeyMetric 
                value="32.4%" 
                label="Overall bachelor's degree attainment rate"
                color="#1e3a8a"
              />
              <KeyMetric 
                value="+5.2 pts" 
                label="Above California average"
                color="#10b981"
              />
            </KeyMetrics>
          )
        }
        chartContent={
          educationalAttainmentView === 'map' ? (
            <GenericMapTiered
              location={currentLocation}
              lockedVariable="educational_attainment"
              hideVariableSelector={true}
              onDataLoaded={(metrics) => {
                setEducationalAttainmentMetrics(metrics);
              }}
            />
          ) : educationalChartData ? (
            <ChartRendererSupabase
              chartData={educationalChartData.chartData}
              chartOptions={educationalChartData.chartOptions}
              height="300px"
            />
          ) : (
            <div className="placeholder-chart">
              <p>Loading educational attainment data...</p>
            </div>
          )
        }
        chartSelector={{
          options: allowedEducationOptions.map((option) => ({ value: option.value, label: option.label })),
          value: educationalAttainmentView,
          onChange: (value: string) => setEducationalAttainmentView(value as 'chart' | 'map')
        }}
        overviewContent={
          educationalAttainmentView === 'map' && educationalAttainmentMetrics ? (
            <OverviewContent
              title="Bachelor's Degree or Higher Overview"
              description="This map visualizes the percentage of adults 25+ with bachelor's degree or higher across census tracts."
            >
              {[
                `${currentLocation} has an educational attainment rate of ${educationalAttainmentMetrics.currentValue.toFixed(1)}% (adults 25+ with bachelor's degree or higher), compared to the California average of ${educationalAttainmentMetrics.californiaAverage.toFixed(1)}%.`,
                `Educational attainment is a key indicator of community capacity for understanding and participating in climate action initiatives.`,
                `Data is from ${educationalAttainmentMetrics.year} and provides insights into the educational characteristics of communities.`
              ]}
            </OverviewContent>
          ) : (
            <OverviewContent
              title="Educational Summary"
              description="This chart displays educational attainment levels for the selected location, showing the distribution of education levels across the population."
            >
              <p>{currentLocation} demonstrates strong educational outcomes overall, with bachelor's degree attainment rates above the California average. However, significant disparities exist between racial and ethnic groups, highlighting the need for targeted educational equity initiatives.</p>
            </OverviewContent>
          )
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Educational attainment is crucial for climate action planning as it influences community capacity for understanding, implementing, and sustaining climate initiatives."
          >
            <div className="methodology-details-simple">
              <h5>Climate Literacy</h5>
              <p>Higher education levels correlate with increased climate awareness and engagement, making this data essential for designing effective climate education and outreach programs.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Workforce Development</h5>
              <p>Educational attainment patterns inform strategies to build climate action workforce development programs that create pathways for all residents to participate in the green economy.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Educational disparities by race and ethnicity can create barriers to climate action participation and understanding, requiring targeted interventions."
          >
            <div className="methodology-details-simple">
              <h5>Access Barriers</h5>
              <p>Communities with lower educational attainment may face challenges accessing climate information, participating in planning processes, or benefiting from climate action programs.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Inclusive Planning</h5>
              <p>Understanding educational patterns helps ensure climate action planning processes are accessible and relevant to residents across all education levels and demographic groups.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Educational attainment data comes from the American Community Survey, providing detailed statistics on education levels achieved by adults 25 years and older."
            dataSources={[
              'American Community Survey (ACS) 5-year estimates',
              'Adults 25 years and older',
              'Education level categories',
              'Geographic comparisons'
            ]}
            methodology={[
              'Data represents percentage of population with bachelor\'s degree or higher',
              'Calculated from ACS 5-year estimates for geographic areas',
              'Includes comparisons with state and county data when available',
              'Disaggregated by race and ethnicity for equity analysis'
            ]}
          />
        }
        activeView={activeEducationalView}
        onViewChange={toggleEducationalView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        className="alternating-bg"
        onDownloadPNG={() => {
          if (educationalAttainmentView === 'map') {
            console.log('Map PNG download - to be implemented in GenericMapTiered');
            return;
          }
          const canvas = document.querySelector('#educational-attainment-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            link.download = `Educational_Attainment_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          if (educationalAttainmentView === 'map') {
            console.log('Map CSV download - to be implemented in GenericMapTiered');
            return;
          }
          console.log('CSV download for Educational Attainment not yet implemented');
        }}
        pngFilename={`Educational_Attainment_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`Educational_Attainment_Data_${currentLocation.replace(/\s+/g, '_')}`}
      />

      {/* Income by Race Section */}
      <ChartLayout
        scrollAnchorId="chart-income-poverty-by-race"
        title="Income and Poverty by Race"
        chartType="line"
        activeView={activeIncomeView}
        onViewChange={setActiveIncomeView}
        chartSelector={{
          options: allowedIncomeOptions.map((option) => ({ value: option.value, label: option.label })),
          value: activeIncomeRaceChart,
          onChange: setActiveIncomeRaceChart
        }}
        keyMetrics={
          activeIncomeRaceChart === 'income-trends' || activeIncomeRaceChart === 'income-bar' ? (
          <KeyMetrics>
            <KeyMetric 
                value={incomePovertyMetrics.incomeRatio ? incomePovertyMetrics.incomeRatio.toFixed(1) + 'x' : '—'} 
                label="Highest/Lowest Income Ratio" 
              color="#1e3a8a"
            />
            <KeyMetric 
                value={incomePovertyMetrics.lowestIncome ? `$${Math.round(incomePovertyMetrics.lowestIncome).toLocaleString()}` : '—'} 
                label="Lowest Income" 
                color="#dc2626" 
              />
              <KeyMetric 
                value={incomePovertyMetrics.povertyRatio ? incomePovertyMetrics.povertyRatio.toFixed(1) + 'x' : '—'} 
                label="Highest/Lowest Poverty Ratio" 
              color="#10b981"
            />
            </KeyMetrics>
          ) : (
            <KeyMetrics>
            <KeyMetric 
                value={incomePovertyMetrics.povertyRatio ? incomePovertyMetrics.povertyRatio.toFixed(1) + 'x' : '—'} 
                label="Highest/Lowest Poverty Ratio" 
                color="#1e3a8a" 
              />
              <KeyMetric 
                value={incomePovertyMetrics.highestPovertyRate ? `${incomePovertyMetrics.highestPovertyRate.toFixed(1)}%` : '—'} 
                label="Highest Poverty Rate" 
              color="#dc2626"
              />
              <KeyMetric 
                value={incomePovertyMetrics.incomeRatio ? incomePovertyMetrics.incomeRatio.toFixed(1) + 'x' : '—'} 
                label="Highest/Lowest Income Ratio" 
                color="#10b981" 
            />
          </KeyMetrics>
          )
        }
        chartContent={
          activeIncomeRaceChart === 'income-trends' ? (
            <div id="income-by-race-trends-chart">
              <IncomeByRaceTrends key={currentLocation} location={currentLocation} />
            </div>
          ) : activeIncomeRaceChart === 'poverty-trends' ? (
            <div id="poverty-by-race-trends-chart">
              <PovertyByRaceTrends location={currentLocation} />
            </div>
          ) : activeIncomeRaceChart === 'income-bar' ? (
            <div id="income-by-race-bar-chart">
              <IncomeByRaceBar key={currentLocation} location={currentLocation} />
            </div>
          ) : (
            <div id="poverty-by-race-bar-chart">
              <PovertyByRaceBar location={currentLocation} />
            </div>
          )
        }
        onDownloadPNG={() => {
          const chartId = activeIncomeRaceChart === 'income-trends' ? '#income-by-race-trends-chart' :
                          activeIncomeRaceChart === 'poverty-trends' ? '#poverty-by-race-trends-chart' :
                          activeIncomeRaceChart === 'income-bar' ? '#income-by-race-bar-chart' :
                          '#poverty-by-race-bar-chart';
          const canvas = document.querySelector(`${chartId} canvas`) as HTMLCanvasElement;
          if (canvas) {
            const filename = activeIncomeRaceChart === 'income-trends' ? 'Income_by_Race_Trends' :
                            activeIncomeRaceChart === 'poverty-trends' ? 'Poverty_by_Race_Trends' :
                            activeIncomeRaceChart === 'income-bar' ? 'Income_by_Race_Bar' :
                            'Poverty_by_Race_Bar';
            const link = document.createElement('a');
            link.download = `${currentLocation.replace(/\s+/g, '_')}_${filename}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          console.log(`CSV download for ${activeIncomeRaceChart} not yet implemented`);
        }}
        pngFilename={`${currentLocation.replace(/\s+/g, '_')}_Income_Poverty_by_Race`}
        csvFilename={`${currentLocation.replace(/\s+/g, '_')}_Income_Poverty_Data`}
        overviewContent={
          activeIncomeRaceChart === 'income-trends' ? (
          <OverviewContent
              title="Income Trends Overview"
              description="This chart displays median household income trends by race and ethnicity over time. It reveals whether income gaps between racial groups are widening or narrowing, providing critical context for understanding economic disparities that affect climate action participation."
            />
          ) : activeIncomeRaceChart === 'poverty-trends' ? (
            <OverviewContent
              title="Poverty Trends Overview"
              description="This chart displays poverty rates by race and ethnicity over time, revealing structural inequalities that impact climate vulnerability and resilience. Tracking trends shows whether gaps between racial groups are widening or narrowing."
            />
          ) : activeIncomeRaceChart === 'income-bar' ? (
            <OverviewContent
              title="Income by Race Overview"
              description="This bar chart displays median household income by race and ethnicity for the most recent year available. It provides a snapshot of current economic disparities, showing which racial groups have the highest and lowest median incomes in the selected location."
            />
          ) : (
            <OverviewContent
              title="Poverty by Race Overview"
              description="This bar chart displays poverty rates by race and ethnicity for the most recent year available. It reveals which racial groups face the highest poverty rates, highlighting economic disparities that create barriers to climate action participation and increase vulnerability to climate impacts."
            />
          )
        }
        relevanceContent={
          activeIncomeRaceChart === 'income-trends' ? (
          <RelevanceContent
            title="Relevance to Climate Action"
              description="Income disparities by race shape residents' ability to participate in and benefit from climate policies and clean technology adoption."
          >
            <div className="methodology-details-simple">
                <h5>Technology Access</h5>
                <p>Income directly influences access to clean technologies like electric vehicles, home energy retrofits, and rooftop solar panels. Lower-income households face barriers to these solutions without targeted support.</p>
            </div>
            <div className="methodology-details-simple">
                <h5>Climate Resilience</h5>
                <p>Households with higher incomes can better afford climate adaptation measures such as home weatherization, air conditioning, and disaster preparedness, while lower-income communities face greater exposure to climate risks.</p>
            </div>
          </RelevanceContent>
          ) : activeIncomeRaceChart === 'poverty-trends' ? (
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Poverty rates by race reveal structural inequities that shape how different communities experience climate risks and access climate solutions."
          >
            <div className="methodology-details-simple">
              <h5>Climate Vulnerability</h5>
                <p>Low-income communities and communities of color face greater exposure to climate risks such as extreme heat, flooding, and air pollution while lacking resources for adaptation measures.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Resource Access</h5>
                <p>Poverty limits access to clean technologies, energy-efficient housing, and climate resilience measures. Without targeted assistance, low-income households cannot participate in climate programs.</p>
            </div>
          </RelevanceContent>
          ) : activeIncomeRaceChart === 'income-bar' ? (
            <RelevanceContent
              title="Relevance to Climate Action"
              description="Current income levels by race directly shape residents' ability to invest in climate solutions and participate in climate action programs."
          >
            <div className="methodology-details-simple">
                <h5>Investment Capacity</h5>
                <p>Higher-income households can more readily invest in energy-efficient appliances, electric vehicles, and home electrification, while lower-income households require financial assistance to access these technologies.</p>
            </div>
            <div className="methodology-details-simple">
                <h5>Program Participation</h5>
                <p>Income disparities affect participation in climate programs. Targeted incentives and income-qualified rebates are needed to ensure equitable access across all racial groups.</p>
            </div>
            </RelevanceContent>
          ) : (
          <RelevanceContent
            title="Relevance to Climate Action"
              description="Poverty rates by race directly impact communities' capacity to participate in climate solutions and their vulnerability to climate risks."
          >
            <div className="methodology-details-simple">
                <h5>Financial Barriers</h5>
                <p>High poverty rates indicate communities with limited financial resources to invest in clean technologies, energy efficiency upgrades, or climate adaptation measures without substantial assistance.</p>
            </div>
            <div className="methodology-details-simple">
                <h5>Climate Vulnerability</h5>
                <p>Communities with higher poverty rates are often more exposed to climate risks due to older housing stock, limited access to cooling and heating systems, and limited resources for disaster preparedness.</p>
            </div>
          </RelevanceContent>
          )
        }
        equityContent={
          activeIncomeRaceChart === 'income-trends' ? (
          <EquityContent
            title="Equity Considerations"
              description="Income gaps by race reflect historical and systemic inequities that climate policies must address to ensure equitable benefits and participation."
          >
            <div className="methodology-details-simple">
              <h5>Historical Context</h5>
                <p>Discriminatory practices in housing, employment, and lending have created persistent wealth gaps across racial groups that continue to limit access to climate solutions.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Policy Design</h5>
                <p>Climate policies that ignore income disparities risk reinforcing inequality by primarily benefiting higher-income households. Targeted programs with income-based assistance are essential for equity.</p>
            </div>
          </EquityContent>
          ) : activeIncomeRaceChart === 'poverty-trends' ? (
          <EquityContent
            title="Equity Considerations"
            description="Racial disparities in poverty reflect long-standing systemic injustices that require targeted interventions in climate action planning."
          >
            <div className="methodology-details-simple">
              <h5>Systemic Barriers</h5>
                <p>Historical discrimination in housing, employment, and lending has created persistent wealth gaps that limit climate resilience and compound vulnerability to climate impacts.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Targeted Solutions</h5>
                <p>Climate policies must address root causes of poverty while ensuring equitable access to climate benefits and resources through income-qualified programs and community-focused initiatives.</p>
            </div>
          </EquityContent>
          ) : activeIncomeRaceChart === 'income-bar' ? (
            <EquityContent
              title="Equity Considerations"
              description="Income disparities by race must be addressed through equitable program design that prioritizes communities with lower median incomes."
          >
            <div className="methodology-details-simple">
                <h5>Program Prioritization</h5>
                <p>Climate programs should prioritize communities with lower median incomes to ensure that all residents can benefit from clean energy and climate resilience measures, regardless of race.</p>
            </div>
            <div className="methodology-details-simple">
                <h5>Barrier Removal</h5>
                <p>Financial barriers to climate solutions disproportionately affect communities of color with lower incomes. Policies must include direct financial assistance and low-cost financing options.</p>
            </div>
            </EquityContent>
          ) : (
          <EquityContent
            title="Equity Considerations"
              description="Addressing poverty disparities by race is essential for achieving equitable climate outcomes and ensuring all communities can participate in and benefit from climate action."
          >
            <div className="methodology-details-simple">
                <h5>Priority Communities</h5>
                <p>Racial groups with higher poverty rates should be prioritized for climate programs, financial assistance, and community-based climate resilience initiatives to address historical inequities.</p>
            </div>
            <div className="methodology-details-simple">
                <h5>Comprehensive Approach</h5>
                <p>Climate policies must go beyond technology deployment to address underlying economic disparities, ensuring that climate action contributes to poverty reduction and economic opportunity for all racial groups.</p>
            </div>
          </EquityContent>
          )
        }
        methodologyContent={
          activeIncomeRaceChart === 'income-trends' ? (
          <MethodologyContent
            title="Methodology"
              description="Data are from the US Census American Community Survey 5-year estimates (ACS5) via the median_income_by_race table in Supabase. Median household incomes are calculated and reported by race and ethnicity for the selected location."
            dataSources={[
                'US Census American Community Survey 5-year estimates (ACS5)',
                'Supabase table: median_income_by_race',
                'Race/ethnicity classifications follow US Census definitions'
            ]}
            methodology={[
                'Resolve geofips for selected location',
                'Query median_income_by_race table by geoid',
                'Group data by race_or_ethnicity and year',
                'Plot median_income values as time series lines by race'
              ]}
            />
          ) : activeIncomeRaceChart === 'poverty-trends' ? (
            <MethodologyContent
              title="Methodology"
              description="Data are sourced from the US Census American Community Survey 5-year estimates (ACS5) via the poverty_by_race table in Supabase. Poverty rates are calculated as the percentage of individuals in each racial group whose household income falls below the federal poverty threshold."
              dataSources={[
                'US Census American Community Survey 5-year estimates (ACS5)',
                'Supabase table: poverty_by_race',
                'Poverty thresholds vary by household size and location',
                'Race/ethnicity classifications follow US Census definitions'
              ]}
              methodology={[
                'Resolve geofips for selected location',
                'Query poverty_by_race table by geoid',
                'Group data by race and year',
                'Plot percentage values as time series lines by race'
              ]}
            />
          ) : activeIncomeRaceChart === 'income-bar' ? (
            <MethodologyContent
              title="Methodology"
              description="Data are from ACS5 via the median_income_by_race table in Supabase. The chart displays median household income values for the most recent year available, grouped by race and ethnicity."
              dataSources={[
                'US Census American Community Survey 5-year estimates (ACS5)',
                'Supabase table: median_income_by_race',
                'Race/ethnicity classifications follow US Census definitions'
              ]}
              methodology={[
                'Resolve geofips for selected location',
                'Query median_income_by_race table by geoid',
                'Identify latest available year',
                'Retrieve median_income values for all races in that year',
                'Display as grouped bar chart, sorted by income value'
              ]}
            />
          ) : (
          <MethodologyContent
            title="Methodology"
              description="Data are from ACS5 via the poverty_by_race table in Supabase. The chart displays poverty rates (percentage of individuals in poverty) for the most recent year available, grouped by race and ethnicity."
            dataSources={[
                'US Census American Community Survey 5-year estimates (ACS5)',
                'Supabase table: poverty_by_race',
                'Poverty thresholds vary by household size and location',
                'Race/ethnicity classifications follow US Census definitions'
            ]}
            methodology={[
                'Resolve geofips for selected location',
                'Query poverty_by_race table by geoid',
                'Identify latest available year',
                'Retrieve percentage values for all races in that year',
                'Display as grouped bar chart, sorted by poverty rate'
              ]}
            />
          )
        }
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
      />

      {/* Economic Sector Wages Section */}
      <ChartLayout
        scrollAnchorId="chart-job-wages"
        title="Economic Sector Wages"
        chartType="bar"
        activeView={activeJobWagesNarrative}
        onViewChange={(view: string) =>
          setActiveJobWagesNarrative(view as 'overview' | 'relevance' | 'equity' | 'methodology')
        }
        chartSelector={{
          options: allowedJobWagesOptions.map((option) => ({ value: option.value, label: option.label })),
          value: activeJobWagesView,
          onChange: setActiveJobWagesView
        }}
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value={jobWagesMetrics.highestWage ? `$${Math.round(jobWagesMetrics.highestWage).toLocaleString()}` : '—'} 
              label="Highest Wage Sector" 
              color="#10b981"
            />
            <KeyMetric 
              value={jobWagesMetrics.lowestWage ? `$${Math.round(jobWagesMetrics.lowestWage).toLocaleString()}` : '—'} 
              label="Lowest Wage Sector" 
              color="#f59e0b"
            />
            <KeyMetric 
              value={jobWagesMetrics.wageGapRatio ? jobWagesMetrics.wageGapRatio.toFixed(1) + 'x' : '—'} 
              label="Wage Gap Ratio" 
              color="#1e3a8a"
            />
            {jobWagesMetrics.sectorsAboveCA !== null && (
              <KeyMetric 
                value={`${jobWagesMetrics.sectorsAboveCA}/5`} 
                label="Sectors Above CA Average" 
                color="#3b82f6"
              />
            )}
          </KeyMetrics>
        }
        chartContent={
          activeJobWagesView === 'current' ? (
            <div id="job-wages-current-chart">
              <JobWagesBySector location={currentLocation} />
            </div>
          ) : activeJobWagesView === 'trends' ? (
            <div id="job-wages-trends-chart">
              <JobWagesBySectorTrends location={currentLocation} />
            </div>
          ) : activeJobWagesView === 'median-trend' ? (
            <div id="job-wages-median-trend-chart">
              <JobWageMedianTrendComparison location={currentLocation} />
            </div>
          ) : (
            <div id="job-wages-comparison-chart">
              <JobWagesBySectorComparison location={currentLocation} />
            </div>
          )
        }
        overviewContent={
          <OverviewContent
            title="Economic Sector Wages Overview"
            description="This chart displays median income by major job sector, revealing which industries provide good-paying jobs and which workers may be vulnerable to economic transitions, including climate-related changes."
          >
            {jobWagesMetrics.highestWage && jobWagesMetrics.lowestWage ? (
              <p>
                In {jobWagesMetrics.latestYear}, {currentLocation} shows significant variation in wages across economic sectors. 
                The highest-paying sector offers median wages of ${jobWagesMetrics.highestWage.toLocaleString()}, 
                while the lowest-paying sector has ${jobWagesMetrics.lowestWage.toLocaleString()}, 
                creating a wage gap ratio of {jobWagesMetrics.wageGapRatio?.toFixed(1)}x. 
                {jobWagesMetrics.sectorsAboveCA !== null && jobWagesMetrics.sectorsAboveCA > 0 && (
                  <> Compared to California's average, {currentLocation} exceeds state averages in {jobWagesMetrics.sectorsAboveCA} out of 5 sectors.</>
                )}
                Understanding the wage distribution across different economic sectors helps identify opportunities for green job development and workers who may need support during climate transitions. Sectors with lower wages often face higher energy cost burdens, while high-wage sectors indicate economic strengths that can be leveraged for climate action.
              </p>
            ) : (
              <p>
                Understanding the wage distribution across different economic sectors helps identify opportunities for green job development and workers who may need support during climate transitions. Sectors with lower wages often face higher energy cost burdens, while high-wage sectors indicate economic strengths that can be leveraged for climate action.
              </p>
            )}
            <p>The chart shows median annual income for full-time, year-round workers across major occupation categories including professional/technical jobs, service occupations, sales/office work, construction/maintenance, and production/transportation roles.</p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Economic sector wages inform just transition planning, green job development, and equitable climate policy design."
          >
            <div className="methodology-details-simple">
              <h5>Green Job Opportunities</h5>
              <p>The professional and technical sectors (which include renewable energy, energy efficiency, and sustainable design jobs) typically offer competitive wages. Identifying wage levels in climate-friendly sectors helps demonstrate that the transition to a clean energy economy can provide good-paying career opportunities.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Just Transition Planning</h5>
              <p>Workers in traditional energy-intensive sectors (construction, production, transportation) may need retraining and support as the economy shifts to clean energy. Understanding current wage levels helps design transition programs that maintain or improve workers' economic security.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Energy Burden Context</h5>
              <p>Lower-wage sectors (service, retail, food preparation) often face higher energy costs as a percentage of income. This makes energy efficiency programs, weatherization assistance, and clean energy incentives critical for economic equity alongside climate action.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Wage disparities across economic sectors have direct implications for climate justice and equitable policy design."
          >
            <div className="methodology-details-simple">
              <h5>Economic Vulnerability</h5>
              <p>Workers in lower-wage sectors face compounding challenges: higher energy cost burdens, limited ability to invest in electric vehicles or home solar, and greater vulnerability to climate impacts like extreme heat. Climate policies must ensure these workers benefit from clean energy transitions through good jobs and direct assistance, not just higher costs.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Program Design</h5>
              <p>Income-qualified programs for energy efficiency, solar installations, and EV rebates should be calibrated to sector wage levels to ensure accessibility for workers across all occupation types. Workforce development programs should target pathways from lower-wage to higher-wage green sectors.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Community Benefits</h5>
              <p>Climate investments should prioritize creating quality jobs accessible to workers currently in lower-wage sectors, with clear career pathways, benefits, and living wages that support families and communities.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Data Source & Methodology"
            description="Job wage data are from the U.S. Census Bureau American Community Survey 5-Year estimates, providing reliable median income information by occupation category. Data availability varies by location - county-level data is most comprehensive while some city-level data may be limited. Includes only full-time, year-round workers (50+ weeks and 35+ hours per week). Chart shows major occupation categories; detailed subsector breakdowns are available in the source data. Values are in nominal dollars for the survey year."
            dataSources={[
              'U.S. Census Bureau American Community Survey 5-Year Estimates (ACS5)',
              'Table S2411: Occupation by Median Earnings',
              'Supabase table: job_by_wage_level',
              'Occupation categories follow U.S. Census Standard Occupational Classification (SOC) system'
            ]}
            methodology={[
              'Resolve geofips for selected location (county or city)',
              'Query job_by_wage_level table filtered to latest available year',
              'Extract major occupation categories (top-level sectors only)',
              'Display median annual earnings for full-time, year-round workers',
              'Sort by wage level from highest to lowest',
              'Color-code by relative wage level within location'
            ]}
          />
        }
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        onDownloadPNG={() => {
          const chartId = activeJobWagesView === 'current' ? '#job-wages-current-chart' :
                          activeJobWagesView === 'trends' ? '#job-wages-trends-chart' :
                          activeJobWagesView === 'median-trend' ? '#job-wages-median-trend-chart' :
                          '#job-wages-comparison-chart';
          const canvas = document.querySelector(`${chartId} canvas`) as HTMLCanvasElement;
          if (canvas) {
            const filename = activeJobWagesView === 'current' ? 'Job_Wages_Current' :
                            activeJobWagesView === 'trends' ? 'Job_Wages_Trends' :
                            activeJobWagesView === 'median-trend' ? 'Job_Wages_Median_Trend' :
                            'Job_Wages_Comparison';
            const link = document.createElement('a');
            link.download = `${currentLocation.replace(/\s+/g, '_')}_${filename}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          console.log(`CSV download for ${activeJobWagesView} not yet implemented`);
        }}
        pngFilename={`${currentLocation.replace(/\s+/g, '_')}_Economic_Sector_Wages`}
        csvFilename={`${currentLocation.replace(/\s+/g, '_')}_Job_Wages_Data`}
      />

      {/* People-Related Maps */}
      <div
        style={{
          order: getSectionOrder('people_maps'),
          display: peopleMapsEnabled ? 'block' : 'none'
        }}
      >
      <ChartLayout
          title="People-Related Maps"
          description="Interactive maps for demographic indicators such as median household income, political leanings, and population density."
          chartType="map"
          layout="vertical"
          keyMetrics={
            peopleMapMetrics ? (
              <KeyMetrics>
                <KeyMetric
                  value={
                    peopleMapMetrics.variable === 'income'
                      ? `$${Math.round(peopleMapMetrics.currentValue).toLocaleString()}`
                      : peopleMapMetrics.variable === 'political_lean'
                      ? peopleMapMetrics.currentValue > 0
                        ? `D +${peopleMapMetrics.currentValue.toFixed(1)}`
                        : peopleMapMetrics.currentValue < 0
                        ? `R +${Math.abs(peopleMapMetrics.currentValue).toFixed(1)}`
                        : 'Even'
                      : peopleMapMetrics.variable === 'population_density'
                      ? `${Math.round(peopleMapMetrics.currentValue).toLocaleString()} per sq mi`
                      : `${Math.round(peopleMapMetrics.currentValue).toLocaleString()}`
                  }
                  label={
                    peopleMapMetrics.variable === 'income'
                      ? 'Median Household Income'
                      : peopleMapMetrics.variable === 'political_lean'
                      ? 'Political Lean'
                      : peopleMapMetrics.variable === 'population_density'
                      ? 'Population Density'
                      : 'Value'
                  }
                />
                <KeyMetric
                  value={
                    peopleMapMetrics.variable === 'income'
                      ? `$${Math.round(peopleMapMetrics.californiaAverage).toLocaleString()}`
                      : peopleMapMetrics.variable === 'political_lean'
                      ? peopleMapMetrics.californiaAverage > 0
                        ? `D +${peopleMapMetrics.californiaAverage.toFixed(1)}`
                        : peopleMapMetrics.californiaAverage < 0
                        ? `R +${Math.abs(peopleMapMetrics.californiaAverage).toFixed(1)}`
                        : 'Even'
                      : peopleMapMetrics.variable === 'population_density'
                      ? `${Math.round(peopleMapMetrics.californiaAverage).toLocaleString()} per sq mi`
                      : `${Math.round(peopleMapMetrics.californiaAverage).toLocaleString()}`
                  }
                  label="California Average"
                />
              </KeyMetrics>
            ) : undefined
          }
          chartContent={
            <GenericMapTiered
              location={currentLocation}
              onDataLoaded={handlePeopleMapDataLoaded}
            allowedVariables={allowedPeopleMapVariables}
              enableScopeSelector={true}
            />
          }
          overviewContent={
            peopleMapMetrics ? (
              <OverviewContent
                title={
                  peopleMapMetrics.variable === 'income'
                    ? 'Median Household Income Map Overview'
                    : peopleMapMetrics.variable === 'political_lean'
                    ? 'Political Lean Map Overview'
                    : 'Population Density Map Overview'
                }
                description={`This map visualizes ${peopleMapMetrics.variable === 'income' ? 'median household income' : peopleMapMetrics.variable === 'political_lean' ? 'political leanings' : 'population density'} across census tracts in ${currentLocation}, helping identify spatial variation in key demographic indicators.`}
              >
                {peopleMapMetrics.variable === 'income' ? [
                  `${currentLocation} has a median household income of $${Math.round(peopleMapMetrics.currentValue).toLocaleString()}, compared to the California average of $${Math.round(peopleMapMetrics.californiaAverage).toLocaleString()}.`,
                  'Income patterns reveal spatial disparities that affect access to climate solutions, transportation options, and resilience resources.',
                  'Understanding income distribution helps identify communities that may need targeted assistance for climate action programs.'
                ] : peopleMapMetrics.variable === 'political_lean' ? [
                  `${currentLocation} has a political lean of ${peopleMapMetrics.currentValue > 0 ? `D +${peopleMapMetrics.currentValue.toFixed(1)}` : peopleMapMetrics.currentValue < 0 ? `R +${Math.abs(peopleMapMetrics.currentValue).toFixed(1)}` : 'Even'}, compared to the California average of ${peopleMapMetrics.californiaAverage > 0 ? `D +${peopleMapMetrics.californiaAverage.toFixed(1)}` : peopleMapMetrics.californiaAverage < 0 ? `R +${Math.abs(peopleMapMetrics.californiaAverage).toFixed(1)}` : 'Even'}.`,
                  peopleMapMetrics.currentValue > peopleMapMetrics.californiaAverage 
                    ? `This indicates that ${currentLocation} has stronger Democratic leanings than the state average, suggesting potential support for climate action policies.`
                    : `This indicates that ${currentLocation} has weaker Democratic leanings than the state average, highlighting the importance of bipartisan climate solutions.`,
                  'Political leanings data helps identify communities that may be more or less supportive of climate action policies and can inform tailored climate communication strategies.'
                ] : [
                  `${currentLocation} has a population density of ${Math.round(peopleMapMetrics.currentValue).toLocaleString()} persons per square mile, compared to the California average of ${Math.round(peopleMapMetrics.californiaAverage).toLocaleString()} persons per square mile.`,
                  'Population density patterns influence transportation demand, infrastructure needs, and per-capita resource consumption.',
                  'Denser areas often have lower per-capita emissions due to more efficient land use and transportation options.',
                  'Understanding density patterns helps inform compact development strategies and transit-oriented planning.'
                ]}
              </OverviewContent>
            ) : undefined
          }
          relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description={
              peopleMapMetrics?.variable === 'income'
                ? "Household income directly influences access to climate solutions and resilience resources."
                : peopleMapMetrics?.variable === 'political_lean'
                ? "How political leanings data informs climate action planning and policy development."
                : "Population density directly impacts transportation emissions, infrastructure efficiency, and resource consumption patterns."
            }
          >
            {peopleMapMetrics?.variable === 'income' ? (
              <div className="methodology-details-simple">
                <h5>Technology Access</h5>
                <p>Higher-income households can more readily invest in energy-efficient appliances, electric vehicles, and home electrification. Lower-income communities face barriers to clean technology adoption and may need financial assistance programs.</p>
              </div>
            ) : peopleMapMetrics?.variable === 'political_lean' ? (
              <>
                <div className="methodology-details-simple">
                  <h5>Policy Support</h5>
                  <p>Political leanings data helps identify communities that may be more or less supportive of climate action policies, enabling tailored communication strategies.</p>
                </div>
                <div className="methodology-details-simple">
                  <h5>Coalition Building</h5>
                  <p>Understanding political preferences helps identify opportunities for bipartisan climate solutions and effective policy implementation.</p>
                </div>
              </>
            ) : (
              <>
                <div className="methodology-details-simple">
                  <h5>Transportation Efficiency</h5>
                  <p>Higher-density areas typically have lower per-capita transportation emissions due to shorter trip distances and better transit access.</p>
                </div>
                <div className="methodology-details-simple">
                  <h5>Infrastructure Planning</h5>
                  <p>Population density informs transit planning, active transportation infrastructure, and compact development strategies that reduce emissions.</p>
                </div>
              </>
            )}
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description={
              peopleMapMetrics?.variable === 'income'
                ? "Income disparities create barriers to climate action participation and increase vulnerability to climate impacts."
                : peopleMapMetrics?.variable === 'political_lean'
                ? "Equity implications and considerations for political leanings data in climate planning."
                : "Population density patterns can reflect historical development patterns and influence access to services and opportunities."
            }
          >
            {peopleMapMetrics?.variable === 'income' ? (
              <div className="methodology-details-simple">
                <h5>Access Barriers</h5>
                <p>Lower-income communities often face higher energy cost burdens and have limited resources for climate adaptation measures. Climate policies must include income-qualified programs and financial assistance to ensure equitable access to clean technologies.</p>
              </div>
            ) : peopleMapMetrics?.variable === 'political_lean' ? (
              <div className="methodology-details-simple">
                <h5>Policy Design</h5>
                <p>Climate policies should be designed to work across political divides and ensure benefits reach all communities, regardless of political preferences.</p>
              </div>
            ) : (
              <div className="methodology-details-simple">
                <h5>Service Access</h5>
                <p>Density patterns may reflect historical zoning and development policies that created inequitable access to services. Equitable climate action requires ensuring all density levels have access to clean transportation options and efficient infrastructure.</p>
              </div>
            )}
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description={
              peopleMapMetrics?.variable === 'income'
                ? "Median household income data from the American Community Survey with geographic visualization."
                : peopleMapMetrics?.variable === 'political_lean'
                ? "Political Lean analysis based on American Community Survey data with geographic visualization."
                : "Population density calculated from census tract population and land area data."
            }
            dataSources={
              peopleMapMetrics?.variable === 'income'
                ? [
                    'American Community Survey 5-Year Estimates (ACS5)',
                    'U.S. Census Bureau demographic data',
                    'California census tract boundaries',
                    'Median household income (B19013_001E)'
                  ]
                : peopleMapMetrics?.variable === 'political_lean'
                ? [
                    'American Community Survey 5-Year Estimates (ACS5)',
                    'U.S. Census Bureau demographic data',
                    'California census tract boundaries',
                    'Political lean estimates (Democrat minus Republican)'
                  ]
                : [
                    'American Community Survey 5-Year Estimates (ACS5)',
                    'U.S. Census Bureau population data',
                    'Census tract boundaries and land area',
                    'Population density = population / land area (square miles)'
                  ]
            }
            methodology={
              peopleMapMetrics?.variable === 'income'
                ? [
                    'Data represents median household income for each census tract',
                    'Geographic visualization shows income distribution across census tracts',
                    'Comparison calculated relative to California state average',
                    '5-year estimates provide more reliable data for smaller geographic areas',
                    'Income values adjusted for inflation and reported in current dollars'
                  ]
                : peopleMapMetrics?.variable === 'political_lean'
                ? [
                    'Political lean: Democrat minus Republican vote share in recent elections',
                    'Data represents the political orientation of each census tract',
                    'Geographic visualization shows political preferences across census tracts',
                    'Comparison calculated relative to California state average',
                    '5-year estimates provide more reliable data for smaller geographic areas',
                    'Based on recent federal and state election results'
                  ]
                : [
                    'Population data from ACS 5-year estimates for each census tract',
                    'Land area calculated from census tract boundary geometries',
                    'Density calculated as persons per square mile',
                    'Geographic visualization shows density distribution across census tracts',
                    'Comparison calculated relative to California state average',
                    '5-year estimates provide more reliable data for smaller geographic areas'
                  ]
            }
          />
        }
        activeView={activePeopleMapView}
        onViewChange={(view: string) =>
          setActivePeopleMapView(view as 'overview' | 'relevance' | 'equity' | 'methodology')
        }
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
      />
      </div>

      <CustomSectionsForPage pageId="people" locationName={currentLocation} />
      <CustomDatasetsForPage pageId="people" locationName={currentLocation} />
    </div>
  );
};
